/**
 * Browser-only wishlist (localStorage, no account sync — build plan decision D3).
 * Stores an array of product handles. Any number of <wishlist-button>,
 * <wishlist-icon>, and <wishlist-drawer-component> elements on the page stay
 * in sync via a single 'wishlist:change' event dispatched on `document`.
 */

const STORAGE_KEY = 'nc-wishlist';
const CHANGE_EVENT = 'wishlist:change';

const WishlistStore = {
  /** @returns {string[]} */
  getAll() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  },

  /** @param {string} handle */
  has(handle) {
    return this.getAll().includes(handle);
  },

  /** @param {string} handle */
  add(handle) {
    const handles = this.getAll();
    if (!handles.includes(handle)) {
      handles.push(handle);
      this._save(handles);
    }
  },

  /** @param {string} handle */
  remove(handle) {
    const handles = this.getAll().filter((h) => h !== handle);
    this._save(handles);
  },

  /** @param {string} handle */
  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  /** @param {string[]} handles */
  _save(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (_) {
      // Storage unavailable (private browsing, quota, etc.) — fail silently.
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
  },
};

/**
 * Heart toggle button. Works on product cards and the product page.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle ?? '';
    this.button = this.querySelector('.wishlist-button__toggle');
    this.label = this.querySelector('.wishlist-button__label');

    this.#render();

    this.button?.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#onExternalChange);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGE_EVENT, this.#onExternalChange);
  }

  #onClick = (event) => {
    event.preventDefault();
    if (!this.handle) return;
    WishlistStore.toggle(this.handle);
    this.#render();
  };

  #onExternalChange = () => {
    this.#render();
  };

  #render() {
    const active = this.handle ? WishlistStore.has(this.handle) : false;
    this.dataset.active = String(active);
    this.button?.setAttribute('aria-pressed', String(active));

    const translations = window.Theme?.translations ?? {};
    const activeLabel = translations.wishlist_remove ?? 'Remove from wishlist';
    const inactiveLabel = translations.wishlist_add ?? 'Add to wishlist';
    const text = active ? activeLabel : inactiveLabel;

    this.button?.setAttribute('aria-label', text);
    if (this.label) this.label.textContent = text;
  }
}

/**
 * Header icon: shows a live count bubble and toggles the wishlist drawer.
 */
class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('.wishlist-icon__bubble');
    this.bubbleCount = this.querySelector('.cart-bubble__text-count');
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  #onChange = () => {
    this.#render();
  };

  #render() {
    const count = WishlistStore.getAll().length;
    if (!this.bubble || !this.bubbleCount) return;
    this.bubbleCount.textContent = count > 0 ? String(count) : '';
    this.bubble.classList.toggle('hidden', count === 0);
    this.classList.toggle('wishlist-icon--has-items', count > 0);
  }
}

/**
 * Populates the wishlist drawer by fetching each saved product's public
 * JSON (`/products/{handle}.js`) client-side and rendering minimal cards.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list
 * @property {HTMLElement} empty
 */
class WishlistDrawerComponent extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-wishlist-list]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.#renderList();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
    document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
  }

  #onChange = () => {
    this.#renderList();
  };

  #onDrawerOpen = (event) => {
    if (event.target?.id !== 'wishlist-drawer') return;
    this.#renderList();
  };

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  async #renderList() {
    const handles = WishlistStore.getAll();

    if (!this.list || !this.empty) return;

    this.classList.toggle('wishlist-drawer--empty', handles.length === 0);

    if (handles.length === 0) {
      this.list.innerHTML = '';
      this.empty.hidden = false;
      return;
    }

    this.empty.hidden = true;

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch (_) {
          return null;
        }
      })
    );

    this.list.innerHTML = '';

    products.forEach((product, index) => {
      const handle = handles[index];
      if (!product) {
        // Product no longer exists / unavailable — drop it from the saved list.
        WishlistStore.remove(handle);
        return;
      }

      const item = document.createElement('div');
      item.className = 'wishlist-drawer__item';

      const image = product.featured_image || (product.images && product.images[0]) || '';
      const price = this.#formatMoney(product.price);

      item.innerHTML = `
        <a href="${product.url}" class="wishlist-drawer__item-link">
          ${
            image
              ? `<img src="${image}" alt="${product.title}" width="72" height="90" loading="lazy" class="wishlist-drawer__item-image">`
              : ''
          }
          <span class="wishlist-drawer__item-details">
            <span class="wishlist-drawer__item-title">${product.title}</span>
            <span class="wishlist-drawer__item-price">${price}</span>
          </span>
        </a>
        <button type="button" class="wishlist-drawer__item-remove button-unstyled" aria-label="${
          window.Theme?.translations?.wishlist_remove ?? 'Remove from wishlist'
        }">
          &times;
        </button>
      `;

      item.querySelector('.wishlist-drawer__item-remove')?.addEventListener('click', () => {
        WishlistStore.remove(handle);
      });

      this.list?.append(item);
    });
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
